/**
 * Control settings — how the player aims and fires.
 *
 * Janeks: *"Add an option to aim with right mouse and to shoot with space.
 * e.g. on a laptop without a mouse it is impossible to shoot."*
 *
 * ─── The problem, precisely ─────────────────────────────────────────────────
 * Combat is "hold the right mouse button to aim, left-click to attack". On a
 * trackpad the right button IS a two-finger press being physically held down,
 * and there is no second finger left to left-click with. The scheme is not
 * merely awkward there — it is unperformable, and nothing on screen says so.
 *
 * ─── Additive, and on by default ────────────────────────────────────────────
 * `fireWithSpace` ADDS Space as a fire key; it never takes the mouse away. Two
 * consequences, both deliberate:
 *
 *  - **Default on.** The complaint is that a laptop player cannot shoot at all,
 *    and a fix that only works once they find a settings screen does not fix
 *    that. Shipping it on means the game is playable on a trackpad out of the
 *    box, and the setting is there to turn it OFF if Space ever collides with
 *    something the player would rather it did.
 *  - **Never either/or.** An exclusive "Space instead of mouse" mode would let
 *    someone switch it on, sit down at a desktop, and find left-click dead with
 *    no obvious cause. Adding a key cannot strand anyone.
 *
 * Aiming is unchanged — the right mouse button already does it, and the
 * setting's label says so, because half the reported problem is that nobody
 * knew what the controls were.
 *
 * ─── Why localStorage and not the account ───────────────────────────────────
 * Same reasoning as `audio-settings.ts`: this is a property of the DEVICE, not
 * the player. The same person wants Space on the laptop and may not care on the
 * desktop, and a preference that followed the account would be wrong on one of
 * them. Core's `PATCH /me/preferences` takes locale and currency only.
 */
import { createStore } from '../../core/state/store';

export interface ControlSettings {
  /**
   * Space fires while aiming, in addition to the left mouse button.
   *
   * Only ever while AIMING — Space does nothing on its own. Binding it
   * unconditionally would fire the moment a player tapped it out of habit
   * expecting to jump, and there is no jump in this game to explain it.
   */
  fireWithSpace: boolean;
}

const STORAGE_KEY = 'ah.control-settings';

const DEFAULTS: ControlSettings = {
  fireWithSpace: true,
};

export const controlSettingsStore = createStore<ControlSettings>(load());

/**
 * Reads the stored settings, falling back to the defaults on anything
 * unexpected — localStorage is hand-editable and survives across versions, so
 * a stored `fireWithSpace: "yes"` must not reach the input layer.
 *
 * `=== false` rather than `Boolean(...)`: the default is ON, so anything that
 * is not an explicit stored `false` should resolve to the default. A truthy
 * check would turn a corrupted value into "off", silently removing the key the
 * player is relying on.
 */
function load(): ControlSettings {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<ControlSettings>;
    return { fireWithSpace: parsed?.fireWithSpace !== false };
  } catch {
    // Unparseable JSON, or storage blocked entirely (private mode, a sandboxed
    // iframe). Defaults are a working state; throwing here would take the
    // settings screen down with it.
    return { ...DEFAULTS };
  }
}

function persist(settings: ControlSettings): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Storage full or blocked. The in-memory store is still right for this
    // session, which is the half the player can see.
  }
}

export function setFireWithSpace(enabled: boolean): void {
  controlSettingsStore.set((s) => {
    const next = { ...s, fireWithSpace: enabled };
    persist(next);
    return next;
  });
}

export function toggleFireWithSpace(): void {
  setFireWithSpace(!controlSettingsStore.get().fireWithSpace);
}

/**
 * Does this key event mean "attack"?
 *
 * The one place the binding is expressed, so the settlement and the arena can
 * never disagree about it. Both views call it from their own `keydown`, AFTER
 * their own typing/panel guards — this function deliberately knows nothing
 * about whether the player is allowed to act right now, only about the key.
 *
 * `e.code === 'Space'` rather than `e.key === ' '`: `key` is layout-dependent
 * and is a literal space character, which is easy to mistype as an empty string
 * and impossible to spot in review. `code` names the physical key.
 */
export function isFireKey(e: KeyboardEvent, settings: ControlSettings = controlSettingsStore.get()): boolean {
  return settings.fireWithSpace && e.code === 'Space';
}

export function resetControlSettings(): void {
  controlSettingsStore.set({ ...DEFAULTS });
}
